import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "click"];

/**
 * Admin idle timeout gate — shows a warning modal after `warnAfterMs` of inactivity
 * and calls `onLogout()` after `logoutAfterMs`. Only mounts under authenticated admin routes.
 */
export default function IdleTimeout({
  onLogout,
  warnAfterMs = 25 * 60 * 1000,    // 25 min
  logoutAfterMs = 30 * 60 * 1000,  // 30 min
}) {
  const [warned, setWarned] = useState(false);
  const [remaining, setRemaining] = useState(logoutAfterMs - warnAfterMs);
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearAllTimers = () => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);
  };

  const scheduleTimers = () => {
    clearAllTimers();
    warnTimerRef.current = setTimeout(() => {
      setWarned(true);
      setRemaining(logoutAfterMs - warnAfterMs);
      countdownRef.current = setInterval(() => {
        setRemaining((r) => Math.max(0, r - 1000));
      }, 1000);
    }, warnAfterMs);
    logoutTimerRef.current = setTimeout(() => {
      onLogout?.();
    }, logoutAfterMs);
  };

  const resetIdle = () => {
    lastActivityRef.current = Date.now();
    if (!warned) scheduleTimers();
  };

  useEffect(() => {
    scheduleTimers();
    const handler = () => resetIdle();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handler));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!warned) return null;

  const stayLoggedIn = () => {
    setWarned(false);
    resetIdle();
  };

  const min = Math.floor(remaining / 60000);
  const sec = Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0");

  return (
    <div
      data-testid="idle-timeout-modal"
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm grid place-items-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-timeout-title"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-stone-200 max-w-md w-full p-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--vv-gold-deep)]">Security</div>
            <h2 id="idle-timeout-title" className="font-display text-2xl mt-1">Still there?</h2>
          </div>
          <button
            data-testid="idle-timeout-close"
            onClick={stayLoggedIn}
            className="text-stone-400 hover:text-stone-700"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
        <p className="mt-4 text-sm text-stone-600 leading-relaxed">
          For security, we&apos;ll sign you out after 30 minutes of inactivity. You&apos;ll be logged out in{" "}
          <span data-testid="idle-timeout-countdown" className="font-semibold text-stone-900">{min}:{sec}</span>.
        </p>
        <div className="mt-6 flex flex-wrap gap-2 justify-end">
          <button
            data-testid="idle-timeout-logout"
            onClick={onLogout}
            className="vv-btn-outline text-sm"
          >
            Logout now
          </button>
          <button
            data-testid="idle-timeout-stay"
            onClick={stayLoggedIn}
            className="vv-btn-primary text-sm"
          >
            Stay logged in
          </button>
        </div>
      </div>
    </div>
  );
}
